import { useNavigation } from "@react-navigation/native"
import { StackNavigationProp } from "@react-navigation/stack"
import { Button, functions, logger, SMSValidationCodeInput, useAlert, useTheme } from "capsule"
import _ from "lodash"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Platform, StyleSheet, View } from "react-native"
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view"
import { Text } from "react-native-paper"

import { OnCheckActivationCodeParams } from "../../common/types"
import { DOCTUP_CODE_LENGTH, INPUT_HEIGHT } from "../../features/config/Constants"
import {
  ActivationCodeNS,
  NewClientOnboardingNS,
  RegisterActivationCodeNS,
} from "../../features/i18n/constants"
import { OnboardingParamList } from "../../features/Navigation/types"
import { useCode } from "../../features/Providers/CodeProvider"
import { useRole } from "../../features/Providers/RoleProvider"

const length = DOCTUP_CODE_LENGTH

const RegisterActivationCodeScreen = () => {
  const {
    colors: {
      surface: { background },
    },
    dimensions: { spacing },
  } = useTheme()
  const navigation =
    useNavigation<StackNavigationProp<OnboardingParamList, "RegisterActivationCode">>()
  const { t } = useTranslation(RegisterActivationCodeNS)
  const { t: tNewClient } = useTranslation(NewClientOnboardingNS)
  const s = useMemo(
    () => ({
      view: [
        styles.view,
        {
          backgroundColor: background,
          paddingHorizontal: spacing * 2,
        },
      ],
      viewContent: {
        gap: 24,
        marginTop: spacing * 2,
      },
      input: styles.input,
      buttonContainer: {
        marginTop: spacing * 2,
        alignItems: "center" as const,
      },
    }),
    [background, spacing],
  )
  const [loading, setLoading] = useState(false)
  const { role, setRole } = useRole()
  const { code, setCode } = useCode()
  const { showSnack } = useAlert()

  const onValidate = async (val: string) => {
    try {
      if (length === val.length) {
        setLoading(true)
        const checkCodeParams: OnCheckActivationCodeParams = {
          code: val,
          dev: __DEV__,
          phone: "",
          // @ts-ignore
          role,
        }
        const result = await functions().httpsCallable<
          OnCheckActivationCodeParams,
          { workflow: string }
        >("checkActivationCodeValid")(checkCodeParams)
        setCode?.(val)
        switch (result.data.workflow) {
          case "lca":
            setRole?.("patient")
            navigation.navigate("RegisterPecanWorkflowExplanation")
            break
          case "practician":
            navigation.navigate("RegisterPracticianWorkflow")
            break
          case "patient":
            setRole?.("patient")
            navigation.navigate("RegisterCgu")
            break
          case "surgeon":
            setRole?.("surgeon")
            navigation.navigate("RegisterCgu")
            break
          case "kine":
            setRole?.("kine")
            navigation.navigate("RegisterCgu")
            break
          case "gp":
            setRole?.("gp")
            navigation.navigate("RegisterCgu")
            break
          default:
            throw Error("Invalid Workflow")
        }
      }
    } catch (e) {
      logger("Activation code error: ", e.message)
      showSnack({
        message: t([
          `${ActivationCodeNS}:errors.${e.message}`,
          `${ActivationCodeNS}:errors.unknown`,
        ]),
      })
    } finally {
      setLoading(false)
    }
  }
  return (
    <KeyboardAwareScrollView
      style={s.view}
      enableOnAndroid
      keyboardShouldPersistTaps="handled"
      contentContainerStyle={s.viewContent}
    >
      <View style={styles.section}>
        <Text style={styles.textBold}>{t("explanation")}</Text>
        <Text>{t("oneTime")}</Text>
      </View>
      <SMSValidationCodeInput
        focus
        keyboardType="default"
        clear={_.isEmpty(code)}
        inputStyle={s.input}
        editable={!loading}
        {...{ length, onValidate }}
      />
      <View style={s.buttonContainer}>
        <Button
          mode="contained"
          onPress={() =>
            navigation.navigate("NewClientNavigator", {
              screen: "NewClientSelectProfile",
            })
          }
          testID="button_no_code"
        >
          {tNewClient("noCode")}
        </Button>
      </View>
    </KeyboardAwareScrollView>
  )
}

const styles = StyleSheet.create({
  view: {
    flex: 1,
  },
  section: {
    gap: 8,
  },
  textBold: {
    fontWeight: "bold",
    fontSize: 14,
  },
  input: {
    height: INPUT_HEIGHT,
    textAlign: Platform.select({
      ios: "center",
      android: undefined,
      web: "center",
    }),
  },
})

export default RegisterActivationCodeScreen
